// src/encounter/lib/to-drug-interaction-input.ts
// Builds the request body for the drug-interaction check that runs inline at medication entry
// (PROD parity — Flutter `EPrescriptionBloc.getDrugInteractions`). The wire body is Flutter's
// `DrugInteractionInputModel` (subject / performer / dxCode[] / drugInfo[]); it POSTs to the
// interaction engine (Scrips.Patient EPrescription service `/PrescriptionAlert`) which returns the
// persisted-shape `PatientDrugInteraction[]`.
//
// Flutter field mapping (eprescription_bloc.dart getDrugInteractions):
//   dxCode:   diagnosis  → DxCode(dxId: e.identifier, code: e.description, displayName: e.title)
//   drugInfo: medicines  → QuantityCode(code: e.description, display: e.title, system: e.system,
//                                        quantityValue: e.quantity?.toString() ?? "1")
// where a diagnosis's `description` is its ICD code and a medication's `description` is its med code.

/** One diagnosis coding in the DI request (Flutter `DxCode`). */
export interface DrugInteractionDxCode {
  dxId?: string;
  /** The ICD code (Flutter DxCode.code = diagnosis.description). */
  code?: string;
  /** The diagnosis label (Flutter DxCode.displayName = diagnosis.title). */
  displayName?: string;
}

/** One medication coding in the DI request (Flutter `QuantityCode`). */
export interface DrugInteractionDrugInfo {
  /** The medication code (Flutter QuantityCode.code = medication.description). */
  code?: string;
  /** The medication label (Flutter QuantityCode.display = medication.title). */
  display?: string;
  /** The coding system (e.g. DDC). */
  system?: string;
  /** Dose count as a string; Flutter defaults to "1" when absent. */
  quantityValue?: string;
}

/** Request body — Flutter `DrugInteractionInputModel`. POSTed to `/api/EPriscription/PrescriptionAlert`. */
export interface DrugInteractionInput {
  /** patientId (Flutter subject). */
  subject?: string;
  /** providerId (Flutter performer). */
  performer?: string;
  dxCode?: DrugInteractionDxCode[];
  drugInfo?: DrugInteractionDrugInfo[];
}

/** A medication to check (the one being entered, plus any already on the plan). */
export interface DrugInteractionMedInput {
  code: string;
  display?: string;
  system?: string;
  quantity?: string | number;
}

/** A diagnosis providing clinical context for drug-disease interaction detection. */
export interface DrugInteractionDiagnosisInput {
  identifier?: string;
  /** The ICD code. */
  code: string;
  display?: string;
}

/**
 * Server GUID shape. The backend binds `PrescriptionAlert.DxCode[].DxId` as `Guid?`, so a
 * client-side diagnosis id that is NOT a GUID (a newly-added Dx keyed by its ICD code, or a
 * `dx-<label>` fallback — see `handleAddDx`) would 400 the whole request before the interaction
 * check runs. Only a real, server-assigned GUID may go on the wire; anything else is omitted so an
 * unsaved diagnosis still contributes its `code`/`displayName` for drug-disease detection.
 */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Build the DI request body from the encounter context. Mirrors Flutter's `getDrugInteractions`
 * mapping 1:1. `quantity` defaults to "1" (Flutter parity) when a med carries no dose count yet.
 */
export function buildDrugInteractionInput(args: {
  patientId?: string;
  providerId?: string;
  diagnoses?: DrugInteractionDiagnosisInput[];
  medications: DrugInteractionMedInput[];
}): DrugInteractionInput {
  return {
    subject: args.patientId,
    performer: args.providerId,
    dxCode: (args.diagnoses ?? []).map((d) => ({
      // Omit dxId unless it is a server GUID (a newly-added Dx is keyed by ICD code, which the
      // `Guid?` binding rejects with a 400 — dropping the whole interaction check).
      ...(d.identifier && GUID_RE.test(d.identifier) ? { dxId: d.identifier } : {}),
      code: d.code,
      displayName: d.display,
    })),
    drugInfo: args.medications.map((m) => ({
      code: m.code,
      display: m.display,
      system: m.system,
      quantityValue: m.quantity != null && String(m.quantity).trim() !== '' ? String(m.quantity) : '1',
    })),
  };
}
